"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT } from "@/components/tool-kit/styles";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { PlannerPageArt } from "@/components/tools/study-more/planner-page-art";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  addDays,
  type CivilDate,
  parseIsoDate,
  type WeekStart,
} from "@/lib/tool-kit/printables/calendar";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";
import {
  layoutPlannerPage,
  type PlannerLayout,
  weekDays,
  weekStartOf,
} from "@/lib/tool-kit/study-more/weekly-planner";

const ACCENT = "#1f4e79";
const WEEK_CHOICES = ["1", "2", "4", "8"] as const;
const START_HOURS = ["5", "6", "7", "8", "9", "10"] as const;
const END_HOURS = ["16", "18", "20", "21", "22", "24"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const WEEKLY_PLANNER_TEMPLATE_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  layoutLabel: "Layout",
  layouts: {
    grid: "Time grid — a column per day, a row per hour",
    lists: "Day boxes — a ruled box for each day",
  },
  titleLabel: "Title",
  defaultTitle: "Weekly Planner",
  weekLabel: "Week of",
  weekStartLabel: "Week starts on",
  weekStarts: { monday: "Monday", sunday: "Sunday" },
  daysLabel: "Days",
  days: { all: "All seven days", weekdays: "Monday to Friday" },
  startDateLabel: "Date in the first week (optional)",
  startDateHint:
    "Fill it in to print dates on every day; leave it empty for a blank planner you date by hand.",
  invalidDate: "That date could not be read, so the planner is undated.",
  weeksLabel: "Weeks (pages)",
  bandLabel: "Priorities and notes boxes at the top",
  priorityLabel: "Top priorities",
  notesLabel: "Notes",
  startHourLabel: "Day starts at",
  endHourLabel: "Day ends at",
  halfHoursLabel: "Show half-hour guide lines",
  previewLabel: "Weekly planner preview",
  fileName: "weekly-planner",
} as const;

function hourFormatter(locale: string) {
  const format = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    timeZone: "UTC",
  });
  return (hour: number) => format.format(new Date(Date.UTC(2000, 0, 1, hour)));
}

export default function WeeklyPlannerTemplateWidget() {
  const strings = useToolUi(WEEKLY_PLANNER_TEMPLATE_STRINGS);
  const locale = useToolLocale();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [layout, setLayout] = useState<PlannerLayout>("grid");
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [weekStart, setWeekStart] = useState<"monday" | "sunday">("monday");
  const [days, setDays] = useState<"all" | "weekdays">("all");
  const [startDate, setStartDate] = useState("");
  const [weeks, setWeeks] = useState<string>("1");
  const [band, setBand] = useState(true);
  const [startHour, setStartHour] = useState<string>("7");
  const [endHour, setEndHour] = useState<string>("21");
  const [halfHours, setHalfHours] = useState(true);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("landscape");

  const geometry = pageGeometry(paper, orientation);
  const weekStartDay: WeekStart = weekStart === "sunday" ? 0 : 1;
  const parsed = startDate ? parseIsoDate(startDate) : null;
  const invalid = startDate !== "" && parsed === null;
  const pageTotal = Number(weeks);
  const first = Number(startHour);
  const last = Math.max(first + 1, Number(endHour));

  const formatHour = useMemo(() => hourFormatter(locale), [locale]);
  const hourLabels = useMemo(
    () =>
      Array.from({ length: last - first }, (_, index) =>
        formatHour(first + index)
      ),
    [formatHour, first, last]
  );

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
    [locale]
  );

  const pages = Array.from({ length: pageTotal }, (_, index) => {
    const base: CivilDate | null = parsed
      ? addDays(weekStartOf(parsed, weekStartDay), index * 7)
      : null;
    const week = weekDays(locale, weekStartDay, days === "weekdays", base);
    const toDate = (date: CivilDate) =>
      dateFormat.format(new Date(Date.UTC(date.year, date.month, date.day)));
    const rangeLabel =
      week.first && week.last
        ? `${toDate(week.first)} – ${toDate(week.last)}`
        : "";
    const page = layoutPlannerPage(geometry, measure, {
      layout,
      title: title.trim() || strings.defaultTitle,
      weekLabel: strings.weekLabel,
      rangeLabel,
      dayNames: week.dayNames,
      dateLabels: week.dateLabels,
      showBand: band,
      priorityLabel: strings.priorityLabel,
      notesLabel: strings.notesLabel,
      hourLabels,
      halfHours,
    });
    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: pages are positional and never reordered
      <PlannerPageArt accent={ACCENT} key={index} page={page} />
    );
  });

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className={`${CARD} grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}>
          <SelectField
            id="wp-layout"
            label={strings.layoutLabel}
            onChange={setLayout}
            options={(["grid", "lists"] as const).map((value) => ({
              value,
              label: strings.layouts[value],
            }))}
            value={layout}
          />
          <TextField
            id="wp-title"
            label={strings.titleLabel}
            onChange={setTitle}
            value={title}
          />
          <SelectField
            id="wp-week-start"
            label={strings.weekStartLabel}
            onChange={setWeekStart}
            options={(["monday", "sunday"] as const).map((value) => ({
              value,
              label: strings.weekStarts[value],
            }))}
            value={weekStart}
          />
          <SelectField
            id="wp-days"
            label={strings.daysLabel}
            onChange={setDays}
            options={(["all", "weekdays"] as const).map((value) => ({
              value,
              label: strings.days[value],
            }))}
            value={days}
          />
          <TextField
            hint={strings.startDateHint}
            id="wp-date"
            label={strings.startDateLabel}
            onChange={setStartDate}
            type="date"
            value={startDate}
          />
          <SelectField
            id="wp-weeks"
            label={strings.weeksLabel}
            onChange={setWeeks}
            options={WEEK_CHOICES.map((value) => ({ value, label: value }))}
            value={weeks}
          />
          {layout === "grid" ? (
            <>
              <SelectField
                id="wp-start-hour"
                label={strings.startHourLabel}
                onChange={setStartHour}
                options={START_HOURS.map((value) => ({
                  value,
                  label: formatHour(Number(value)),
                }))}
                value={startHour}
              />
              <SelectField
                id="wp-end-hour"
                label={strings.endHourLabel}
                onChange={setEndHour}
                options={END_HOURS.map((value) => ({
                  value,
                  label: formatHour(Number(value) % 24),
                }))}
                value={endHour}
              />
            </>
          ) : null}
          <PaperControls
            idPrefix="wp"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={band}
            label={strings.bandLabel}
            onChange={setBand}
          />
          {layout === "grid" ? (
            <Checkbox
              checked={halfHours}
              label={strings.halfHoursLabel}
              onChange={setHalfHours}
            />
          ) : null}
        </div>
        {invalid ? <Notice>{strings.invalidDate}</Notice> : null}
        <PrintBar
          fileName={`${strings.fileName}-${layout}-${paper}`}
          pageTotal={pageTotal}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
        <p className={HINT}>{strings.previewLabel}</p>
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-4xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
